<script setup lang="ts">
	const { isMobile } = useMobile()
	const showSideMenu = ref(false)
	const { kycIncomplete } = reactor(useUser())
	const { accountInfo } = useAuth()
	const { confirmAccountVerification, setKyc } = useUser()
	const route = useRoute()
	const dashboardEl = ref<HTMLElement | null>(null)
	const previousState = computed(() => String(history.state.back))
	// Trigger user account information
	const processAccount = () => {
		if (accountInfo.value) {
			confirmAccountVerification()
			if (!accountInfo.value?.customer?.meta?.sector && Boolean(accountInfo.value?.email_verified_at)) {
				setKyc(true)
			}
		}
	}

	onMounted(() => {
		showSideMenu.value = !isMobile.value
	})

	// Watch each route to prevent the user from doing anything on the platform if they have not completed their kyc or their account isn't verified yet.
	watch(() => route.path, processAccount)
	watch(accountInfo, processAccount, { immediate: true })
</script>

<template>
	<div class="relative">
		<section class="flex w-full overflow-hidden">
			<layout-aside-nav v-model:show="showSideMenu" />

			<section id="dashboardEl" ref="dashboardEl" class="body relative h-screen flex-1 overflow-y-auto">
				<layout-top-nav v-model:side-menu="showSideMenu" />

				<nuxt-error-boundary>
					<div id="dashboard-body" class="relative flex items-start gap-3 xl:justify-between">
						<div class="w-full px-4 py-6 md:py-12 lg:!px-[25px]">
							<slot />
						</div>
					</div>

					<template #error="{ error }">
						<layout-error :title="error?.message" :status-code="500" :description="error?.cause" />
					</template>
				</nuxt-error-boundary>
			</section>
			<layout-notification />
		</section>
		<dialogs-error
			:show="kycIncomplete"
			:header="previousState.includes('buy-plan') ? `You're almost there!` : 'Hey there!'"
			:paragraph="
				previousState.includes('buy-plan')
					? 'To continue with your staff activation and unlock full access to our platform, please complete the KYC for your business.'
					: `We're excited to have you. Complete your business details to access your dashboard and explore our plans today.`
			"
			modal-button-text="Complete Business KYC"
			:use-cancel-btn="false"
			wrapper-class="space-y-5 mt-8 mb-5"
			:icon-props="{ icon: 'alert' }"
			@done="navigateTo({ path: '/update/kyc' })"
		/>
		<forms-update-business-cac />
	</div>
</template>

<style scoped>
	.body {
		-webkit-overflow-scrolling: touch;
	}
</style>
